"use client";

import { AnimatePresence, motion } from "framer-motion";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import React, { useState } from "react";

import styles from "./Accordion.module.css";

type AccordionProps = {
  items: {
    question: string;
    answer: string;
  }[];
};

const Accordion = ({ items }: AccordionProps) => {
  const [expanded, setExpanded] = useState<false | number>(0);

  return (
    <>
      {items.map((item, i) => {
        const { question, answer } = item;
        const isOpen = i === expanded;
        const isList = typeof answer === "string" && answer.includes("\n");
        const answerContent = isList ? answer.split("\n") : [answer];

        return (
          <div className={styles.container} key={i}>
            <motion.div
              initial={false}
              className={styles.header}
              onClick={() => setExpanded(isOpen ? false : i)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 1 }}
            >
              <motion.h3>{question}</motion.h3>
              <motion.span transition={{ duration: 0.3 }}>
                {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </motion.span>
            </motion.div>
            <AnimatePresence initial={false} mode="wait">
              {isOpen && (
                <motion.section
                  className={styles.content}
                  key="content"
                  initial={{ opacity: 0, clipPath: "inset(0% 0% 100% 0%)" }}
                  animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
                  exit={{ opacity: 0, clipPath: "inset(0% 0% 100% 0%)" }}
                  transition={{
                    opacity: { duration: 0.3 },
                    clipPath: { duration: 0.3, ease: "easeInOut" },
                  }}
                  style={{ overflow: "hidden" }}
                >
                  <motion.div
                    className={styles.text}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 10, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isList ? (
                      <motion.ol
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={{
                          visible: { transition: { staggerChildren: 0.05 } },
                          hidden: {},
                        }}
                      >
                        {answerContent.map((step, index) => (
                          <motion.li
                            key={index}
                            variants={{
                              hidden: { opacity: 0, y: 5 },
                              visible: { opacity: 1, y: 0 },
                            }}
                          >
                            {step}
                          </motion.li>
                        ))}
                      </motion.ol>
                    ) : (
                      <p>{answer}</p>
                    )}
                  </motion.div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
};

export default Accordion;
